/**
 * Billing store – single source of truth for artist billing (bank accounts + GST details).
 * Session-based fetch: ensureFetched() runs GET once per session; refetch after PUT/POST/PATCH/DELETE.
 */

import { create } from "zustand";
import { ArtistService } from "@/services/artistService";
import { useArtistDetailsStore } from "@/stores/artistDetailsStore";
import type {
  BillingData,
  GstDetailsPayload,
  BankAccountCreatePayload,
  BankAccountPatchPayload,
} from "@/services/artistService";

export type { BillingData, GstDetailsPayload, BankAccountCreatePayload, BankAccountPatchPayload };

interface BillingState {
  data: BillingData | null;
  isLoading: boolean;
  error: string | null;
  hasFetchedInSession: boolean;

  setData: (data: BillingData | null) => void;
  fetchBilling: () => Promise<BillingData | null>;
  /** Fetches only if !hasFetchedInSession. Call when user opens Billing tab. */
  ensureFetched: () => Promise<BillingData | null>;
  updateGstDetails: (payload: GstDetailsPayload) => Promise<void>;
  createBankAccount: (payload: BankAccountCreatePayload) => Promise<void>;
  updateBankAccount: (id: number, payload: BankAccountPatchPayload) => Promise<void>;
  deleteBankAccount: (id: number) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  /** Call on logout to reset session cache. */
  reset: () => void;
}

let inFlightFetch: Promise<BillingData | null> | null = null;

const initialState = {
  data: null,
  isLoading: false,
  error: null,
  hasFetchedInSession: false,
};

export const useBillingStore = create<BillingState>()((set, get) => ({
  ...initialState,

  setData: (data) => set({ data, error: null }),

  fetchBilling: async () => {
    if (inFlightFetch) return inFlightFetch;
    inFlightFetch = (async () => {
      set({ isLoading: true, error: null });
      try {
        const data = await ArtistService.getBilling();
        set({
          data,
          error: null,
          isLoading: false,
          hasFetchedInSession: true,
        });
        void useArtistDetailsStore.getState().fetchArtistDetails();
        return data;
      } catch (err) {
        set({
          error: "Failed to load billing details",
          isLoading: false,
          data: null,
        });
        return null;
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  ensureFetched: async () => {
    const { hasFetchedInSession, fetchBilling, data } = get();
    if (hasFetchedInSession) return data;
    return fetchBilling();
  },

  updateGstDetails: async (payload) => {
    set({ error: null });
    try {
      await ArtistService.putGstDetails(payload);
      await get().fetchBilling();
    } catch (err) {
      set({ error: "Failed to update GST & PAN details. Please try again." });
      throw err;
    }
  },

  createBankAccount: async (payload) => {
    set({ error: null });
    try {
      await ArtistService.postBankAccount(payload);
      await get().fetchBilling();
    } catch (err) {
      set({ error: "Failed to add bank account. Please try again." });
      throw err;
    }
  },

  updateBankAccount: async (id, payload) => {
    set({ error: null });
    try {
      await ArtistService.patchBankAccount(id, payload);
      await get().fetchBilling();
    } catch (err) {
      set({ error: "Failed to update bank account. Please try again." });
      throw err;
    }
  },

  deleteBankAccount: async (id) => {
    set({ error: null });
    try {
      await ArtistService.deleteBankAccount(id);
      await get().fetchBilling();
    } catch (err) {
      set({ error: "Failed to delete bank account. Please try again." });
      throw err;
    }
  },

  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),

  reset: () => {
    inFlightFetch = null;
    set(initialState);
  },
}));
